import React from 'react';
import { Briefcase, PackageCheck, Weight, Plus, Minus } from 'lucide-react';

/**
 * Компонент секции параметров багажа
 */
const BaggageParamsSection = ({ 
  formData, 
  handleInputChange, 
  errors
}) => {
  // Увеличение/уменьшение количества багажа
  const adjustBaggageCount = (increment) => {
    const currentCount = parseInt(formData.baggageCount) || 0;
    const newCount = Math.max(1, currentCount + increment);
    
    const event = {
      target: {
        name: 'baggageCount',
        value: newCount.toString()
      }
    };
    
    handleInputChange(event);
  };

  // Увеличение/уменьшение количества ручной клади
  const adjustCarryOnCount = (increment) => {
    const currentCount = parseInt(formData.carryOnCount) || 0;
    const newCount = Math.max(1, currentCount + increment);
    
    const event = {
      target: {
        name: 'carryOnCount',
        value: newCount.toString()
      }
    };
    
    handleInputChange(event);
  };

  return (
    <div className="mb-6 bg-gray-50 dark:bg-gray-800/30 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
      <h3 className="text-base font-medium text-gray-900 dark:text-white mb-4 flex items-center">
        <Briefcase className="w-5 h-5 mr-2 text-purple-500" />
        Параметры багажа
      </h3>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Регистрируемый багаж */}
        <div className="bg-white dark:bg-gray-700 p-4 rounded-lg border border-gray-200 dark:border-gray-600">
          <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-3 flex items-center">
            <PackageCheck className="w-4 h-4 mr-2 text-purple-500" />
            Регистрируемый багаж
          </h4>
          
          {/* Вес багажа */}
          <div className="mb-3">
            <label htmlFor="baggageWeight" className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
              Вес багажа (кг)
            </label>
            <input
              type="text"
              id="baggageWeight"
              name="baggageWeight"
              value={formData.baggageWeight}
              onChange={handleInputChange}
              className={`form-input w-full ${errors.baggageWeight ? 'input-error' : ''}`}
              placeholder="Введите вес"
            />
            {errors.baggageWeight && (
              <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                {errors.baggageWeight}
              </p>
            )}
          </div>
          
          {/* Количество мест багажа */}
          <div>
            <label htmlFor="baggageCount" className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
              Количество мест
            </label>
            <div className="flex items-center">
              <button
                type="button"
                onClick={() => adjustBaggageCount(-1)}
                className="h-9 w-9 flex items-center justify-center rounded-l-md border border-gray-300 dark:border-gray-600 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700"
              >
                <Minus className="w-4 h-4" />
              </button>
              <input
                type="text"
                id="baggageCount"
                name="baggageCount"
                value={formData.baggageCount}
                onChange={handleInputChange}
                className={`form-input w-full rounded-none text-center ${errors.baggageCount ? 'input-error' : ''}`}
                placeholder="1"
              />
              <button
                type="button"
                onClick={() => adjustBaggageCount(1)}
                className="h-9 w-9 flex items-center justify-center rounded-r-md border border-gray-300 dark:border-gray-600 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700"
              >
                <Plus className="w-4 h-4" />
              </button>
            </div>
            {errors.baggageCount && (
              <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                {errors.baggageCount}
              </p>
            )}
          </div>
          
          {/* Секция перевеса багажа */}
          <div className="mt-4 pt-3 border-t border-gray-200 dark:border-gray-600">
            <label className="inline-flex items-center">
              <input
                type="checkbox"
                name="includeOverweight"
                checked={formData.includeOverweight}
                onChange={handleInputChange}
                className="form-checkbox"
              />
              <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                Учитывать перевес
              </span>
            </label>
            
            {formData.includeOverweight && (
              <div className="mt-2">
                <label htmlFor="overweightKg" className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Дополнительный вес (кг)
                </label>
                <input
                  type="text"
                  id="overweightKg"
                  name="overweightKg"
                  value={formData.overweightKg}
                  onChange={handleInputChange}
                  className={`form-input w-full ${errors.overweightKg ? 'input-error' : ''}`}
                  placeholder="Введите перевес"
                />
                {errors.overweightKg && (
                  <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                    {errors.overweightKg}
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
        
        {/* Ручная кладь */}
        <div className="bg-white dark:bg-gray-700 p-4 rounded-lg border border-gray-200 dark:border-gray-600">
          <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-3 flex items-center">
            <Weight className="w-4 h-4 mr-2 text-purple-500" />
            Ручная кладь
          </h4>
          
          {/* Вес ручной клади */}
          <div className="mb-3">
            <label htmlFor="carryOnWeight" className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
              Вес ручной клади (кг)
            </label>
            <input
              type="text"
              id="carryOnWeight"
              name="carryOnWeight"
              value={formData.carryOnWeight}
              onChange={handleInputChange}
              className={`form-input w-full ${errors.carryOnWeight ? 'input-error' : ''}`}
              placeholder="Введите вес"
            />
            {errors.carryOnWeight && (
              <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                {errors.carryOnWeight}
              </p>
            )}
          </div>
          
          {/* Количество мест ручной клади */}
          <div>
            <label htmlFor="carryOnCount" className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
              Количество мест
            </label>
            <div className="flex items-center">
              <button
                type="button"
                onClick={() => adjustCarryOnCount(-1)}
                className="h-9 w-9 flex items-center justify-center rounded-l-md border border-gray-300 dark:border-gray-600 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700"
              >
                <Minus className="w-4 h-4" />
              </button>
              <input
                type="text"
                id="carryOnCount"
                name="carryOnCount"
                value={formData.carryOnCount}
                onChange={handleInputChange}
                className={`form-input w-full rounded-none text-center ${errors.carryOnCount ? 'input-error' : ''}`}
                placeholder="1"
              />
              <button
                type="button"
                onClick={() => adjustCarryOnCount(1)}
                className="h-9 w-9 flex items-center justify-center rounded-r-md border border-gray-300 dark:border-gray-600 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700"
              >
                <Plus className="w-4 h-4" />
              </button>
            </div>
            {errors.carryOnCount && (
              <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                {errors.carryOnCount}
              </p>
            )}
          </div>
          
          {/* Информация о размерах */}
          <div className="mt-4 bg-blue-50 dark:bg-blue-900/30 p-2 rounded border border-blue-100 dark:border-blue-800">
            <p className="text-xs text-blue-700 dark:text-blue-300">
              <span className="font-medium">Примечание:</span> Стандартные размеры ручной клади 55x40x25 см.
              Уточняйте допустимые габариты на сайте авиакомпании.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BaggageParamsSection;